import camelCase from 'lodash-es/camelCase'
import startCase from 'lodash-es/startCase'
import _truncate from 'lodash-es/truncate'
import { DateTime, DateTimeFormatOptions } from 'luxon'
import { Person } from '@/api/entities/people/types'
import { Subtask } from '@/api/pathways/subtasks/types'
import { displayRelationship } from '@/legacy/components/patient/sidebar/lib/contacts'
import { Communication } from '@/legacy/types/communications/communications'
import { Entity } from '@/legacy/types/entities/entities'
import { Contact } from '@/legacy/types/patients/contacts'
import {
  treatmentStatusMap,
  programStatusMap,
  Patient,
} from '@/legacy/types/patients/patients'
import { stringToDateTime } from '../date/date'

// IMPROVEME(MT-1776): consolidate below name format fns into 1?
/**
 *
 * @param firstName
 * @param lastName
 * @param middleInitial
 */
export function formatName(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  middleInitial: string | null | undefined = undefined
): string {
  const initial = middleInitial ? middleInitial + '. ' : ''
  return `${firstName ?? ''} ${initial}${lastName ?? ''}`
}

/**
 *
 * @param firstName
 * @param lastName
 * @param preferredName
 */
export function formatNameWithPreferredName(
  firstName: string | undefined,
  lastName: string | undefined,
  preferredName: string | undefined = undefined
): string {
  const preferred = preferredName ? `"${preferredName}"` : ''
  const parts = [firstName, preferred, lastName].filter(Boolean)
  return parts.join(' ')
}

/**
 *
 * @param root0
 * @param root0.firstName
 * @param root0.lastName
 * @param root0.middleInitial
 */
export function formatNameFromPerson({
  firstName,
  lastName,
  middleInitial = undefined,
}: Person): string {
  return formatName(firstName, lastName, middleInitial)
}

/**
 * Appends a staff credential suffix to a name, e.g. "Jane Doe" -> "Jane Doe, SW".
 * @param name
 * @param suffix
 */
export function appendSuffix(
  name: string | null | undefined,
  suffix: string | null | undefined
): string {
  if (!name) {
    return ''
  }
  return suffix ? `${name}, ${suffix}` : name
}

/**
 *
 * @param entity
 */
export function formatNameFromEntity(entity: Entity | Patient | null): string {
  if (entity?.person) {
    return formatName(entity.person.firstName, entity.person.lastName)
  } else {
    return ''
  }
}

/**
 *
 * @param contact
 */
export function formatContactNameAndRelationship(contact: Contact): string {
  const contactEntity = contact.contactEntity
  if (contactEntity?.person) {
    const contactName = formatName(
      contactEntity.person.firstName,
      contactEntity.person.lastName
    )
    const relationshipToPatient = displayRelationship(
      contact.relationshipToPatient
    )
    return relationshipToPatient
      ? `${contactName} (${relationshipToPatient})`
      : contactName
  } else {
    return ''
  }
}

/**
 * Truncates the given string at word boundaries.
 * @param s
 * @param characters
 * @deprecated use lodash truncate directly, it has more options
 */
export function truncate(s: string, characters: number) {
  return _truncate(s, { length: characters })
}

/**
 * Creates a "|" separated list of string values, each truncated to the length of truncateTo
 * ex: separateAndTruncate(['foobar', 'fizzbuzz'], 3)
 * output: 'foo... | fiz...'
 * @param values
 * @param truncateTo
 */
export function separateAndTruncate(values: unknown[], truncateTo = 0) {
  return values
    .map((v) => (truncateTo && v ? truncate(v as string, truncateTo) : v))
    .join(' | ')
}

/**
 * convert datetime to display string local
 * @param d Luxon DateTime
 * @param locale locale string
 * @returns string
 */
export function formatDateTime(
  d: DateTime | undefined,
  locale?: DateTimeFormatOptions
) {
  if (d) {
    return d.setZone('local').toLocaleString(locale ?? DateTime.DATE_SHORT)
  }
  return ''
}

/**
 * convert datetime to display string
 * NO ZONE CONVERSION
 * @param d Luxon DateTime
 * @param locale locale string
 * @returns string
 */
export function formatCleanDateTime(
  d: DateTime | undefined,
  locale?: DateTimeFormatOptions
) {
  if (d) {
    return d.toLocaleString(locale ?? DateTime.DATE_SHORT)
  }
  return ''
}

/**
 * convert datetime to display time string
 * NO ZONE CONVERSION
 * @param d Luxon DateTime
 * @param locale locale string
 * @returns string
 */
export function formatCleanTime(
  d: DateTime | undefined,
  locale?: DateTimeFormatOptions
) {
  if (d) {
    return d.toLocaleString(locale ?? DateTime.TIME_SIMPLE)
  }
  return ''
}

/**
 * convert datetime to display string local with time
 * @param d Luxon DateTime
 * @param locale locale string
 * @returns string
 */
export function formatDateTimeWithTime(
  d: DateTime | undefined,
  locale?: DateTimeFormatOptions
) {
  if (d) {
    return `${d
      .setZone('local')
      .toLocaleString(locale ?? DateTime.DATE_SHORT)}, ${d
      .setZone('local')
      .toLocaleString(locale ?? DateTime.TIME_SIMPLE)}`
  }
  return ''
}

/**
 * convert datetime to display string UTC
 * @param d Luxon DateTime
 * @param locale locale string
 * @returns string
 */
export function formatDateTimeUTC(
  d: DateTime | undefined,
  locale?: DateTimeFormatOptions
) {
  if (d) {
    return d.setZone('utc').toLocaleString(locale ?? DateTime.DATE_SHORT)
  }
  return ''
}

/**
 * Find program status display from mapping
 * @param programStatus
 * @param programSubstatus
 * @returns display value or empty string
 */
export function formatProgramStatus(
  programStatus: string,
  programSubstatus: string
): string {
  return programStatusMap[programStatus]?.[programSubstatus] ?? ''
}

/**
 * find treatment status display from mapping
 * @param treatmentStatus
 * @param treatmentSubstatus
 * @returns display value or empty string
 */
export function formatTreatmentStatus(
  treatmentStatus: string,
  treatmentSubstatus: string
): string {
  return treatmentStatusMap[treatmentStatus]?.[treatmentSubstatus] ?? ''
}

/**
 * This function returns a date string set to
 * 8:59PM CST timezone
 * @param date JSDate object
 * @returns 8:59PM CST timezone for date
 */
export function formatDateToCST(date: Date) {
  const zone = 'America/Chicago'
  const month = date.getMonth() + 1
  const day = date.getDate()
  const year = date.getFullYear()
  return DateTime.fromObject({}, { zone })
    .set({
      day,
      month,
      year,
      hour: 20,
      minute: 59,
      second: 59,
      millisecond: 0,
    })
    .toISO()
}

/**
 * Get formatted date of subtask
 * @param subtask
 */
export function formatSubtaskDate(subtask: Subtask) {
  return formatDateTime(stringToDateTime(subtask.statusUpdatedAt))
}

/**
 * Build URL to specific subtask
 * @param urlPath
 * @param subtask
 */
export function buildSubtaskUrl(urlPath: string, subtask: Subtask) {
  return urlPath + '?subtaskId=' + subtask.subtaskId
}

/**
 * Build URL to specific communication
 * @param communication
 */
export function buildCommunicationUrl(communication: Communication) {
  const patientId = communication.patient?.entityId
  const commId = communication.communicationId

  return `${window.location.origin}/patient/${patientId}?commId=${commId}`
}

/**
 * Coalesces values (anything which can be truthy or falsy) to a boolean display string.
 * @param value
 * @param defaultValue
 */
export function visibleDisplayBoolean(
  value: any,
  defaultValue: string | null = 'No'
): string | null {
  const falsyValues = ['', 'no', 'false']

  const TRUE_DISPLAY = 'Yes'
  const FALSE_DISPLAY = 'No'

  if (typeof value === 'string') {
    // Normalize string values to lowercase for comparison
    value = value.toLowerCase()
    if (falsyValues.includes(value)) {
      return FALSE_DISPLAY
    }
  }
  // If the value is truthy (not null, undefined, false, 0, '', etc.), return 'Yes'
  if (value) {
    return TRUE_DISPLAY
  }
  // If the value is explicitly false, return 'No'
  if (value === false) {
    return FALSE_DISPLAY
  }
  // If the value is null or undefined, return the default value
  return defaultValue
}

/**
 * Coalesces values (anything which can be truthy or falsy) to a boolean display string.
 * @param value
 * @param defaultValue
 */
export function visibleDisplayWithPrefersNotToAnswer(
  value: any,
  defaultValue = null
): string | null {
  if (value) {
    return startCase(camelCase(value))
  }
  return defaultValue
}
